import { useCallback } from 'react';
/**
 * Finds the closest parent span with a 'data-word-id' attribute from a given node.
 * @param node The starting node for the search.
 * @returns The found HTMLElement or null.
 */
function getWordSpan(node) {
    if (!node) {
        return null;
    }
    if (node.nodeType === Node.ELEMENT_NODE && node.dataset.wordId) {
        return node;
    }
    return node.parentElement?.closest('span[data-word-id]') ?? null;
}
/**
 * A hook for handling text selection logic within a PDF's custom text layer.
 * It provides rich, precise selection data and handles cross-line selections
 * by truncating them to the starting line.
 */
export function usePdfTextSelection({ onTextSelect, paragraphMap }) {
    const handleSelection = useCallback(() => {
        if (!onTextSelect || !paragraphMap || paragraphMap.size === 0) {
            return;
        }
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            onTextSelect(null);
            return;
        }
        const range = selection.getRangeAt(0);
        const startSpan = getWordSpan(range.startContainer);
        const endSpan = getWordSpan(range.endContainer);
        if (!startSpan) {
            onTextSelect(null);
            return;
        }
        // --- NEW LOGIC: Reconstruct text with character-level precision ---
        let reconstructedText = '';
        if (startSpan && endSpan) {
            if (startSpan === endSpan) {
                // Single word selection: use character offsets
                const wordText = startSpan.textContent || '';
                reconstructedText = wordText.substring(range.startOffset, range.endOffset);
            }
            else if (startSpan.parentElement) {
                // Multi-word selection: start word part + middle words + end word part
                const siblings = Array.from(startSpan.parentElement.children);
                const startIndex = siblings.indexOf(startSpan);
                const endIndex = siblings.indexOf(endSpan);
                if (startIndex > -1 && endIndex > -1) {
                    // Start word: from startOffset to end
                    const startWordText = startSpan.textContent || '';
                    reconstructedText = startWordText.substring(range.startOffset);
                    // Middle words: complete text
                    for (let i = startIndex + 1; i < endIndex; i++) {
                        reconstructedText += ' ' + (siblings[i].textContent || '');
                    }
                    // End word: from start to endOffset
                    const endWordText = endSpan.textContent || '';
                    reconstructedText += ' ' + endWordText.substring(0, range.endOffset);
                }
            }
        }
        // Fallback to the original method if reconstruction fails.
        if (!reconstructedText) {
            reconstructedText = selection.toString().trim();
        }
        // IMPORTANT: Always trim the final reconstructed text to match text selection behavior
        reconstructedText = reconstructedText.trim();
        // --- END NEW LOGIC ---
        const startParagraphId = startSpan.dataset.paragraphId;
        const startWordId = startSpan.dataset.wordId;
        if (!startParagraphId || !startWordId) {
            onTextSelect(null);
            return;
        }
        const startParagraphData = paragraphMap.get(startParagraphId);
        const endParagraphId = endSpan?.dataset.paragraphId;
        let endWordId = endSpan?.dataset.wordId;
        let endWordOffset = range.endOffset;
        // --- Cross-paragraph (i.e., cross-line) selection handling ---
        // If the selection spans multiple paragraphs, truncate it to the end of the start paragraph.
        if (!endSpan || !endParagraphId || !endWordId || startParagraphId !== endParagraphId) {
            if (!startParagraphData || startParagraphData.words.length === 0) {
                onTextSelect(null);
                return;
            }
            const lastWordOfPara = startParagraphData.words[startParagraphData.words.length - 1];
            endWordId = lastWordOfPara.id;
            endWordOffset = lastWordOfPara.text.length;
            // Manually adjust the selection range to visually reflect the truncation
            const newRange = document.createRange();
            newRange.setStart(range.startContainer, range.startOffset);
            const finalEndSpan = document.querySelector(`span[data-word-id='${endWordId}']`);
            if (finalEndSpan?.firstChild) {
                newRange.setEnd(finalEndSpan.firstChild, endWordOffset);
                selection.removeAllRanges();
                selection.addRange(newRange);
                // After truncation, we MUST re-reconstruct the text with character precision.
                if (startSpan && finalEndSpan) {
                    if (startSpan === finalEndSpan) {
                        // Single word selection after truncation
                        const wordText = startSpan.textContent || '';
                        reconstructedText = wordText.substring(range.startOffset, endWordOffset);
                    }
                    else if (startSpan.parentElement) {
                        const siblings = Array.from(startSpan.parentElement.children);
                        const startIndex = siblings.indexOf(startSpan);
                        const endIndex = siblings.indexOf(finalEndSpan);
                        if (startIndex > -1 && endIndex > -1) {
                            // Start word: from startOffset to end
                            const startWordText = startSpan.textContent || '';
                            reconstructedText = startWordText.substring(range.startOffset);
                            // Middle words: complete text
                            for (let i = startIndex + 1; i < endIndex; i++) {
                                reconstructedText += ' ' + (siblings[i].textContent || '');
                            }
                            // End word: from start to endOffset (truncated)
                            const endWordText = finalEndSpan.textContent || '';
                            reconstructedText += ' ' + endWordText.substring(0, endWordOffset);
                        }
                        else {
                            reconstructedText = selection.toString().trim();
                        }
                    }
                    else {
                        reconstructedText = selection.toString().trim();
                    }
                }
                else {
                    reconstructedText = selection.toString().trim();
                }
                // IMPORTANT: Always trim the final reconstructed text after truncation
                reconstructedText = reconstructedText.trim();
            }
        }
        // Convert DOMRectList to a serializable array
        // --- BUG FIX: DO NOT use getClientRects(), as they are relative to the viewport.
        // Instead, get the pre-calculated, page-relative rects from the paragraph data.
        let selectionRects = [];
        if (startParagraphData && startWordId && endWordId) {
            const startIndex = startParagraphData.words.findIndex(w => w.id === startWordId);
            const endIndex = startParagraphData.words.findIndex(w => w.id === endWordId);
            if (startIndex !== -1 && endIndex !== -1) {
                selectionRects = startParagraphData.words.slice(startIndex, endIndex + 1).map(word => {
                    // The word.rect from PreRenderedWord is PdfPlumberWord['rect'],
                    // which has { x0, top, x1, bottom, ... }. We need to adapt it to SerializableRect.
                    const { x0, top, x1, bottom } = word.rect;
                    const width = x1 - x0;
                    const height = bottom - top;
                    return {
                        x: x0,
                        y: top, // Use `top` for the y-coordinate from the top of the page
                        width: width,
                        height: height,
                        top: top,
                        left: x0,
                        right: x1,
                        bottom: bottom,
                    };
                });
            }
        }
        // Normalize word boundaries by handling empty edge spans
        let finalStartWordId = startWordId;
        let finalEndWordId = endWordId;
        let finalStartWordOffset = range.startOffset;
        let finalEndWordOffset = endWordOffset;
        // If we have a multi-word selection, check if edge words are effectively empty
        if (startWordId !== endWordId) {
            // Check if start word selection is effectively empty (at the very end)
            const startWordText = startSpan.textContent || '';
            if (finalStartWordOffset >= startWordText.length) {
                // Start selection is at the end of start word, this might be selecting trailing space
                // Try to find the next non-empty word as the real start
                if (startSpan.parentElement) {
                    const siblings = Array.from(startSpan.parentElement.children);
                    const startIndex = siblings.indexOf(startSpan);
                    for (let i = startIndex + 1; i < siblings.length; i++) {
                        const siblingSpan = siblings[i];
                        const siblingText = siblingSpan.textContent || '';
                        if (siblingText.trim().length > 0) {
                            // Found a non-empty word, use it as start
                            finalStartWordId = siblingSpan.dataset.wordId || startWordId;
                            finalStartWordOffset = 0;
                            break;
                        }
                    }
                }
            }
            // Check if end word selection is effectively empty (at the very beginning)
            if (finalEndWordOffset <= 0) {
                // End selection is at the beginning of end word, this might be selecting leading space
                // Try to find the previous non-empty word as the real end
                if (endSpan && endSpan.parentElement) {
                    const siblings = Array.from(endSpan.parentElement.children);
                    const endIndex = siblings.indexOf(endSpan);
                    for (let i = endIndex - 1; i >= 0; i--) {
                        const siblingSpan = siblings[i];
                        const siblingText = siblingSpan.textContent || '';
                        if (siblingText.trim().length > 0) {
                            // Found a non-empty word, use it as end
                            finalEndWordId = siblingSpan.dataset.wordId || endWordId;
                            finalEndWordOffset = siblingText.length;
                            break;
                        }
                    }
                }
            }
        }
        const selectionData = {
            type: 'pdf',
            selectedText: reconstructedText,
            location: {
                paragraphId: startParagraphId,
                paragraphData: startParagraphData,
                startWordId: finalStartWordId,
                endWordId: finalEndWordId,
                startWordOffset: finalStartWordOffset,
                endWordOffset: finalEndWordOffset,
                selectionRects: selectionRects, // Use the corrected rects
            },
        };
        onTextSelect(selectionData);
    }, [onTextSelect, paragraphMap]);
    return {
        handleSelection,
    };
}
//# sourceMappingURL=usePdfTextSelection.js.map